import flatstore from "flatstore";
import { isNil } from "lodash";
import React, { Fragment, useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import GiftSelectionLoader from "../../../assets/contentLoader/GiftSelectionLoader";
import Image from "../../../components/common/Image";
import Price from "../../../components/product/common/Price";
import {
  getSubscriptionPaths,
  localStorageUtil,
} from "../../../utils/functions";
import { getSubscriptionProductList } from "../../../utils/services";

function GiftSelection({ subscription }) {
  const [data, setData] = useState([]);
  const [selectedGift, setSelectedGift] = useState(null);
  const [submissionError, setSubmissionError] = useState("");
  const hasNavigatedRef = useRef(false); // Track if we've already navigated using ref to prevent re-renders

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const paths = useMemo(() => getSubscriptionPaths(), []); // Memoize paths to prevent re-renders

  const size = localStorageUtil.getItem("subscription")?.size || "";
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const giftSlugFromUrl = useMemo(
    () => queryParams.get("gift_slug") || sessionStorage.getItem("subscription_gift_slug"),
    [queryParams]
  );
  const isResetting = useMemo(() => sessionStorage.getItem("resetting_subscription") === "true", []);

  // Track if gift items exist for current size (for new users)
  const [hasGiftItemsForSize, setHasGiftItemsForSize] = useState(false);

  // Check if gift step should be shown
  // For new users: Show only if gift items exist for the selected size
  // For resetting users: Show only if gift_slug exists in URL (user had gift and it's still valid)
  const shouldShowGiftStep = useMemo(() => {
    if (isResetting) {
      // Resetting subscription: only show if gift_slug exists in URL
      return !!giftSlugFromUrl;
    } else {
      // New subscription: only show if gift items exist for the selected size
      return hasGiftItemsForSize;
    }
  }, [isResetting, giftSlugFromUrl, hasGiftItemsForSize]);

  // Handle navigation when gift step shouldn't be shown
  useEffect(() => {
    if (hasNavigatedRef.current) return;

    // For resetting users: if no gift_slug, skip gift step
    if (isResetting && !giftSlugFromUrl) {
      hasNavigatedRef.current = true;
      const customizePath = paths.steps["customize"] || "/";
      navigate(customizePath, { replace: true });
      return;
    }

    if (!size) {
      hasNavigatedRef.current = true;
      const basePath = paths.base || "/";
      navigate(basePath);
    }
  }, [isResetting, giftSlugFromUrl, size, navigate, paths]);

  // Handle subscription data loading and gift selection logic
  useEffect(() => {
    if (hasNavigatedRef.current) return;

    // Load subscription data if not available
    if (isNil(subscription)) {
      getSubscriptionProductList();
      return;
    }

    if (!subscription || !size) return;

    // Filter gift items (is_gift = 'yes' OR is_partial_gift = 'yes') for the current size
    const filteredData = subscription.filter((item) => {
      return (
        item.subscription_type === "grutto_subscription_custom_" + size &&
        (item.is_gift === "yes" || item.is_partial_gift === "yes") &&
        item.total_stock !== 0
      );
    });

    setData(filteredData);

    // For new users: Check if gift items exist and update state
    if (!isResetting) {
      const hasGifts = filteredData.length > 0;
      setHasGiftItemsForSize(hasGifts);

      // If no gift items for new user, skip gift step and go to customize
      if (!hasGifts) {
        const subscriptionData = localStorageUtil.getItem("subscription") || {};
        const existingItems = subscriptionData.items || {};
        const itemsWithoutGifts = Object.keys(existingItems).reduce((acc, itemId) => {
          if (existingItems[itemId]?.is_gift !== true) {
            acc[itemId] = existingItems[itemId];
          }
          return acc;
        }, {});

        localStorageUtil.updateObjectItem("subscription", {
          items: itemsWithoutGifts,
        });
        hasNavigatedRef.current = true;
        const customizePath = paths.steps["customize"] || "/";
        navigate(customizePath, { replace: true });
        return;
      }
    }

    // For resetting users: If gift_slug exists but no items found, skip gift step
    if (isResetting && giftSlugFromUrl && filteredData.length === 0) {
      const subscriptionData = localStorageUtil.getItem("subscription") || {};
      const existingItems = subscriptionData.items || {};
      const itemsWithoutGifts = Object.keys(existingItems).reduce((acc, itemId) => {
        if (existingItems[itemId]?.is_gift !== true) {
          acc[itemId] = existingItems[itemId];
        }
        return acc;
      }, {});

      localStorageUtil.updateObjectItem("subscription", {
        items: itemsWithoutGifts,
      });
      hasNavigatedRef.current = true;
      const customizePath = paths.steps["customize"] || "/";
      navigate(customizePath, { replace: true });
      return;
    }

    // Only proceed with gift selection if we should show the step
    if (!shouldShowGiftStep) {
      return;
    }

    const subscriptionItems = localStorageUtil.getItem("subscription")?.items || {};

    // Rule 1: Check if gift_slug is provided in URL (highest priority for resetting subscriptions)
    if (giftSlugFromUrl && filteredData.length > 0) {
      const giftItemBySlug = filteredData.find((item) => item.slug === giftSlugFromUrl);
      if (giftItemBySlug) {
        const giftItemId = giftItemBySlug.bundled_item_id;
        setSelectedGift(giftItemId);

        // Save the gift item to localStorage in items with is_gift: true
        const itemsWithoutGifts = Object.keys(subscriptionItems).reduce((acc, itemId) => {
          if (subscriptionItems[itemId]?.is_gift !== true) {
            acc[itemId] = subscriptionItems[itemId];
          }
          return acc;
        }, {});

        // For partial gifts, use the partial_gift_amount, otherwise default to 1
        const giftQuantity = giftItemBySlug.is_partial_gift === "yes"
          ? parseInt(giftItemBySlug.partial_gift_amount, 10) || 1
          : 1;
        // Preserve user's quantity if they already increased it in Customize (e.g. back → gift → customize)
        const existingPartial = subscriptionItems[giftItemId];
        const quantityToUse =
          existingPartial?.is_partial_gift && existingPartial?.quantity != null
            ? Math.max(giftQuantity, parseInt(existingPartial.quantity, 10) || giftQuantity)
            : giftQuantity;

        itemsWithoutGifts[giftItemId] = {
          product_id: giftItemBySlug.meatcut_product_id,
          quantity: quantityToUse,
          is_gift: true,
          is_partial_gift: giftItemBySlug.is_partial_gift === "yes",
          partial_gift_amount: giftItemBySlug.is_partial_gift === "yes" ? (parseInt(giftItemBySlug.partial_gift_amount, 10) || 1) : null,
          title: giftItemBySlug.title,
          weight: giftItemBySlug.weight,
          unit_type: giftItemBySlug.unit_type,
          pieces_per_vac: giftItemBySlug.pieces_per_vac,
          subscription_amount: giftItemBySlug.subscription_amount,
        };

        localStorageUtil.updateObjectItem("subscription", {
          items: itemsWithoutGifts,
        });

        // Don't remove gift_slug from URL - leaving it is harmless and prevents history issues
        // Removing it with replaceState breaks browser back button navigation

        return; // Exit early if we found and set a valid gift from slug
      }
    }

    // Rule 2: Check if user already has a gift item selected in localStorage items
    const existingGiftItemId = Object.keys(subscriptionItems).find((itemId) => {
      return subscriptionItems[itemId]?.is_gift === true;
    });

    if (existingGiftItemId) {
      const giftItemId = parseInt(existingGiftItemId, 10);
      // Verify this gift item still exists in the available gift items
      const giftExists = filteredData.some(
        (item) => item.bundled_item_id === giftItemId
      );
      if (giftExists) {
        setSelectedGift(giftItemId);
        return; // Exit early if we found and set a valid gift from localStorage
      }
    }

    // Rule 3: If no gift item is selected (first time), auto-select the first item as default
    if (filteredData.length > 0) {
      const firstGiftItem = filteredData[0];
      if (firstGiftItem) {
        const firstGiftItemId = firstGiftItem.bundled_item_id;
        setSelectedGift(firstGiftItemId);

        // Automatically save the first item as selected
        const itemsWithoutGifts = Object.keys(subscriptionItems).reduce((acc, itemId) => {
          if (subscriptionItems[itemId]?.is_gift !== true) {
            acc[itemId] = subscriptionItems[itemId];
          }
          return acc;
        }, {});

        // For partial gifts, use the partial_gift_amount, otherwise default to 1
        const giftQuantity = firstGiftItem.is_partial_gift === "yes"
          ? parseInt(firstGiftItem.partial_gift_amount, 10) || 1
          : 1;

        // Add the first gift item with is_gift flag
        itemsWithoutGifts[firstGiftItemId] = {
          product_id: firstGiftItem.meatcut_product_id,
          quantity: giftQuantity,
          is_gift: true,
          is_partial_gift: firstGiftItem.is_partial_gift === "yes",
          partial_gift_amount: firstGiftItem.is_partial_gift === "yes" ? (parseInt(firstGiftItem.partial_gift_amount, 10) || 1) : null,
          title: firstGiftItem.title,
          weight: firstGiftItem.weight,
          unit_type: firstGiftItem.unit_type,
          pieces_per_vac: firstGiftItem.pieces_per_vac,
          subscription_amount: firstGiftItem.subscription_amount,
          product_price: firstGiftItem.product_price,
        };

        // Save updated items to localStorage
        localStorageUtil.updateObjectItem("subscription", {
          items: itemsWithoutGifts,
        });
      }
    }
  }, [subscription, size, shouldShowGiftStep, navigate, paths, giftSlugFromUrl, isResetting]);

  // Hide submissionError after certain time
  useEffect(() => {
    let timeout;
    if (submissionError) {
      timeout = setTimeout(() => {
        setSubmissionError("");
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [submissionError]);

  const handleGiftSelect = (bundledItemId, productId, title, weight, unitType, piecesPerVac, subscriptionAmount, isPartialGift, partialGiftAmount, productPrice) => {
    setSelectedGift(bundledItemId);

    // Get existing items from localStorage
    const subscription = localStorageUtil.getItem("subscription") || {};
    const existingItems = subscription.items || {};

    // Remove any existing gift items first (only one gift allowed)
    const itemsWithoutGifts = Object.keys(existingItems).reduce((acc, itemId) => {
      if (existingItems[itemId]?.is_gift !== true) {
        acc[itemId] = existingItems[itemId];
      }
      return acc;
    }, {});

    // For partial gifts, use the partial_gift_amount, otherwise default to 1
    const giftQuantity = isPartialGift ? (parseInt(partialGiftAmount, 10) || 1) : 1;

    // Add the new gift item with is_gift flag
    itemsWithoutGifts[bundledItemId] = {
      product_id: productId,
      quantity: giftQuantity,
      is_gift: true,
      is_partial_gift: isPartialGift || false,
      partial_gift_amount: isPartialGift ? (parseInt(partialGiftAmount, 10) || 1) : null,
      title: title,
      weight: weight,
      unit_type: unitType,
      pieces_per_vac: piecesPerVac,
      subscription_amount: subscriptionAmount,
      product_price: productPrice ?? null,
    };

    // Save updated items to localStorage
    localStorageUtil.updateObjectItem("subscription", {
      items: itemsWithoutGifts,
    });
  };

  const handleSubmit = () => {
    if (selectedGift) {
      // For resetting subscriptions, preserve history by not using replace
      // This allows proper back button navigation
      navigate(paths.steps["customize"] || "/");
      setSubmissionError("");
    } else {
      setSubmissionError(t("subscription.gift.selection_required"));
    }
  };

  // Progress bar calculation
  const progressBarPercentage = size ? 12.5 : 0; // After size selection
  const progressBarWidth = {
    width: `${progressBarPercentage}%`,
  };

  // Show loader while subscription data is loading (before we know if step should be shown)
  if (isNil(subscription) || !subscription) {
    return (
      <div className="subscription-box">
        <div className="container pt-5 pb-5">
          <div className="row">
            <div className="col-12">
              <GiftSelectionLoader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if we shouldn't show gift step (navigation is handled in useEffect)
  if (!shouldShowGiftStep) {
    return null;
  }

  const isSingleGift = data.length === 1;

  // Show loader when we have subscription but data not yet set (e.g. right after load)
  if (!data.length) {
    return (
      <div className="subscription-box">
        <div className="container pt-5 pb-5">
          <div className="row">
            <div className="col-12">
              <GiftSelectionLoader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="subscription-box">
        <div className="progress-bar">
          <div className="fill" style={progressBarWidth}></div>
        </div>

        {submissionError && (
          <div className="response error">{submissionError}</div>
        )}

        <div className="container">
          <div className="mt-4 text-center">
            <h2 className="heading">
              {isResetting
                ? t("subscription.gift.page.title-update")
                : t("subscription.gift.page.title")}
            </h2>
            {/* <p className="subtitle mt-3">
              {t("subscription.gift.page.subtitle")}
            </p> */}
          </div>
        </div>

        <div className="container farmshop-items">
          <div className="row justify-content-center">
            <div className={`col-12 ${data.length <= 3 ? "col-md-10" : ""}`}>
              <div className={`gift-selection-grid ${data.length < 3 ? "gift-selection-grid-centered" : ""}`}>
                {data.map((item) => {
                  const isSelected = selectedGift !== null && Number(selectedGift) === Number(item.bundled_item_id);
                  const showRadio = !isSingleGift;
                  // Gifts: do not multiply by subscription_amount (gift qty is excluded from subscription multiplier).
                  const giftQty = item.is_partial_gift === "yes"
                    ? (parseInt(item.partial_gift_amount, 10) || 1)
                    : 1;
                  const totalPieces = (item.pieces_per_vac || 1) * giftQty;
                  const piecesText = t("subscription.gift.pieces_format_simple", {
                    pieces_per_vac: totalPieces,
                    weight: item.weight,
                    unit_type: item.unit_type || "g"
                  });

                  // Ribbon savings: 26 * qty * price; qty = partial_gift_amount || default_quantity || 1
                  const ribbonQty = item.is_partial_gift === "yes" && item.partial_gift_amount != null
                    ? (parseInt(item.partial_gift_amount, 10) || 1)
                    : (item.default_quantity != null ? (parseInt(item.default_quantity, 10) || 1) : 1);
                  const price = parseFloat(item.product_price) || 0;
                  const savingsAmount = 26 * ribbonQty * price;

                  return (
                    <div
                      key={item.bundled_item_id}
                      className={`gift-selection-card ${isSelected ? "selected" : ""}${isSingleGift ? " gift-selection-card--single" : ""}`}
                      onClick={
                        showRadio
                          ? () =>
                              handleGiftSelect(
                                item.bundled_item_id,
                                item.meatcut_product_id,
                                item.title,
                                item.weight,
                                item.unit_type,
                                item.pieces_per_vac,
                                item.subscription_amount,
                                item.is_partial_gift === "yes",
                                item.partial_gift_amount,
                                item.product_price
                              )
                          : undefined
                      }
                    >
                      <div className="gift-card-image">
                        <div className="gift-savings-wrap">
                          <div className="gift-savings-box">
                            <span className="gift-savings-label">{t("subscription.gift.ribbon_label")}</span>
                            <span className="gift-savings-value">
                              <Price value={savingsAmount} withCurrency />
                            </span>
                          </div>
                        </div>
                        <Image
                          src={item.image_url}
                          alt={item.title}
                          title={item.title}
                          className="img-fluid"
                        />
                      </div>

                      <div className="gift-card-content">
                        <div className="gift-card-header">
                          <h3 className="gift-card-title">{item.title}</h3>
                          {showRadio && (
                            <div className="gift-card-radio">
                              <input
                                type="radio"
                                name="gift-item"
                                checked={isSelected}
                                onChange={() =>
                                  handleGiftSelect(
                                    item.bundled_item_id,
                                    item.meatcut_product_id,
                                    item.title,
                                    item.weight,
                                    item.unit_type,
                                    item.pieces_per_vac,
                                    item.subscription_amount,
                                    item.is_partial_gift === "yes",
                                    item.partial_gift_amount,
                                    item.product_price
                                  )
                                }
                              />
                              <span className="radio-custom"></span>
                            </div>
                          )}
                        </div>
                        <div className="gift-card-pieces">
                          {piecesText} - {t("subscription.gift.bioTxt")}
                        </div>
                        <p className="gift-card-description">
                          {size === "large"
                            ? t("subscription.gift.free_for_year_large")
                            : t("subscription.gift.free_for_year_small")}
                        </p>
                        {/* {item.bundle_price && (
                          <p className="gift-card-value">
                            {t("subscription.gift.value")}
                          </p>
                        )} */}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="subscription__cta">
          {selectedGift ? (
            <button className="btn btn--red" onClick={handleSubmit}>
              {t("subscription.gift.cta")}
            </button>
          ) : (
            <button className="btn btn--disable">
              {t("subscription.gift.cta")}
            </button>
          )}
        </div>
      </div>
    </Fragment>
  );
}

export default flatstore.connect(["subscription"])(GiftSelection);

